import React, { JSX, useCallback, useEffect, useRef, useState } from "react";
import Layout from "@/components/layout/Layout";
import CampaignCard from "./components/CampaignCard";
import Icon1 from "@/assets/home/card/Icon1.svg";
import Icon2 from "@/assets/home/card/Icon2.svg";
import Icon3 from "@/assets/home/card/Icon3.svg";
import Icon4 from "@/assets/home/card/Icon4.svg";
import Icon5 from "@/assets/home/card/Icon5.svg";
import Icon6 from "@/assets/home/card/Icon6.svg";
import CardImage from "@/assets/Dashboard_Card.png";
import { OnboardingSuccessDialog } from "./components/OnboardingDialog";
import TextField from "@/components/ui/Textfield";
import { SearchIcon } from "lucide-react";
import Dropdown from "@/components/ui/DropDown";
import { Plus } from "lucide-react/";
import { Navigate, useNavigate } from "react-router-dom";
import useAuthStore from "@/store/authStore";
import useDashboardStore, {
  StatusCounts,
} from "./store/dashboardStore";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";

type IconType = {
  src: string;
  alt: string;
  number: number;
};

/**
 * Build the two-column icon groups for a campaign card from its
 * asset-version status counts.
 *
 * Column A: Briefed, In Progress, In Review
 * Column B: Approved, Trafficking, Live
 */
const buildStatusIcons = (counts?: StatusCounts): IconType[][] => {
  const c: StatusCounts = counts ?? {
    briefed: 0,
    in_progress: 0,
    in_review: 0,
    approved: 0,
    trafficking: 0,
    live: 0,
    draft: 0,
    total: 0,
  };

  return [
    [
      { src: Icon2, alt: "Briefed", number: c.briefed },
      { src: Icon3, alt: "In Progress", number: c.in_progress },
      { src: Icon4, alt: "In Review", number: c.in_review },
    ],
    [
      { src: Icon1, alt: "Approved", number: c.approved },
      { src: Icon6, alt: "Trafficking", number: c.trafficking },
      { src: Icon5, alt: "Live", number: c.live },
    ],
  ];
};

export default function DashboardPage(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const user_role = useAuthStore((state) => state.user_role);
  const {
    getCampaign,
    campaignData,
    fetchClientTeams,
    clientTeamOptions,
    searchQuery,
    setSearchQuery,
    selectedClientTeam,
    setSelectedClientTeam,
  } = useDashboardStore();
  const setError = useGlobalStore((state) => state.setError);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchData = useCallback(
    async (q?: string, clientTeam?: string) => {
      setIsLoading(true);
      try {
        await getCampaign({
          q: q || undefined,
          client_team: clientTeam || undefined,
        });
      } catch (error: any) {
        setError(error.message ?? "Something went wrong");
      } finally {
        setIsLoading(false);
      }
    },
    [getCampaign, setError]
  );

  useEffect(() => {
    fetchData();
    fetchClientTeams();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Debounce API call by 400ms
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchData(value, selectedClientTeam);
    }, 400);
  };

  const handleClientFilter = (value: string) => {
    setSelectedClientTeam(value);
    fetchData(searchQuery, value);
  };

  if (user_role === "Global Admin") return <Navigate to="/admin-dashboard" />;

  return (
    <Layout>
      <OnboardingSuccessDialog />

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl md:text-3xl font-semibold text-black mb-4">
          Campaign Dashboard
        </h1>
        <div className="w-full md:w-auto">
          <div className="flex">
            <TextField
              type="text"
              leadingIcon={<SearchIcon size={15} />}
              placeholder="Search By Keywords, Project #"
              className="w-80"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <Dropdown
              options={[{ title: "All Clients", value: "" }, ...clientTeamOptions]}
              selected={selectedClientTeam}
              setSelected={handleClientFilter}
              className="ml-4 w-64"
              placeholder="Filter by Client"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
        <div className="mt-[68px]">
          <div
            className="border border-[#EA33C0] rounded-lg p-8 flex flex-col items-center justify-center text-[#EA33C0] hover:bg-pink-50 transition-colors cursor-pointer h-[330px] shadow-md"
            onClick={() => navigate("/campaign")}
          >
            <Plus className="text-5xl mb-4" />
            <span className="text-lg font-medium">Add campaign</span>
          </div>
        </div>

        {isLoading && (
          <div className="col-span-full flex justify-center items-center h-64">
            <Spinner size={40} />
          </div>
        )}

        {!isLoading &&
          campaignData.length > 0 &&
          campaignData.map((e, index) => (
            <CampaignCard
              imageUrl={e.header_image?.url || CardImage}
              icons={buildStatusIcons(e.status_counts)}
              onViewClick={() => {
                if (e.campaign.progress_status === "campaign") {
                  navigate(`/campaign/flight?id=${e.campaign.id}`);
                } else if (e.campaign.progress_status === "flight") {
                  navigate(`/campaign/assets?id=${e.campaign.id}`);
                } else if (e.campaign.progress_status === "assets") {
                  navigate(`/campaign/${e.campaign.id}`);
                } else {
                  alert("Campaign Clicked");
                }
              }}
              key={index}
              data={e}
            />
          ))}

        {!isLoading && campaignData.length === 0 && (
          <div className="col-span-full text-center text-gray-500 mt-12">
            No campaigns found.
          </div>
        )}
      </div>
    </Layout>
  );
}
